/**
 * Filing frequency — monthly or quarterly (QRMP) — on the client's profile (FR-E16, B-10;
 * ADR-0037's default for ND-E11).
 *
 * An effective-dated fact: each change applies from a quarter's first month and the months
 * before it keep the frequency they had, so a quarter filed quarterly still reads that way after
 * the client goes back to monthly. The service refuses a change from mid-quarter, or one that
 * would strand a return already opened under the other frequency, and says why; this card shows
 * the refusal as it is worded and decides nothing itself (ADR-0014).
 */
import { Alert, Button, Card, Input } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import { ApiError, type FilingFrequency, clients } from '../lib/api'

export const FREQUENCY_WORDS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly (QRMP)',
}

export function frequencyWords(f: Pick<FilingFrequency, 'frequency' | 'since'>): string {
  const name = FREQUENCY_WORDS[f.frequency] ?? f.frequency
  if (f.since) return `${name}, from ${f.since}`
  return `${name} — no change has been recorded`
}

export function FilingFrequencyCard({ clientId }: { clientId: string }) {
  const { can } = useEvaluation()
  const editable = can(CLIENT, 'can_update')

  const [state, setState] = useState<FilingFrequency | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [to, setTo] = useState<'monthly' | 'quarterly'>('quarterly')
  const [from, setFrom] = useState('')
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    try {
      const body = (await clients.filingFrequency(clientId)).data
      setState(body)
      setTo(body.frequency === 'quarterly' ? 'monthly' : 'quarterly')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The filing frequency could not be read.')
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  if (!state) return error ? <Alert tone="error">{error}</Alert> : null

  return (
    <Card className="p-4" data-testid="filing-frequency">
      <p className="gst-panel-h mb-3">Filing frequency</p>
      {error && (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      )}
      <p className="gst-meta mb-3">
        Files <strong>{frequencyWords(state)}</strong>.{' '}
        {state.frequency === 'quarterly'
          ? 'GSTR-1 and GSTR-3B are filed for the quarter; the first two months have an optional IFF and a PMT-06 deposit.'
          : 'GSTR-1 and GSTR-3B are filed every month.'}
      </p>
      {state.history.length > 0 && (
        <ul className="gst-meta mb-3 space-y-1">
          {state.history.map((row) => (
            <li key={row.effective_from}>
              From {row.effective_from}: {FREQUENCY_WORDS[row.frequency] ?? row.frequency}
              {row.note ? ` — ${row.note}` : ''}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="gst-label">
            Change to
            <select
              className="gst-input"
              value={to}
              onChange={(e) => {
                setTo(e.target.value as 'monthly' | 'quarterly')
                setError(null) // N31: a refusal goes when the input it was about changes
              }}
            >
              <option value="quarterly">Quarterly (QRMP)</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          <Input
            label="From (MM-YYYY, a quarter's first month)"
            value={from}
            placeholder="07-2026"
            onChange={(e) => {
              setFrom(e.target.value)
              setError(null)
            }}
          />
          <Input
            label="Note (optional)"
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
              setError(null)
            }}
          />
          <Button
            type="button"
            size="sm"
            disabled={busy || !/^\d{2}-\d{4}$/.test(from)}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                const body = await clients.setFilingFrequency(clientId, {
                  frequency: to,
                  effective_from: from,
                  ...(note.trim() ? { note: note.trim() } : {}),
                })
                setState(body.data)
                setFrom('')
                setNote('')
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'That change did not save.')
              } finally {
                setBusy(false)
              }
            }}
          >
            Record the change
          </Button>
        </div>
      )}
    </Card>
  )
}
